import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { TrustScore } from "@/components/site/TrustScore";
import { adminListTrustScores, adminRefreshAllTrustScores, type TrustScoreRow, type TrustScoreSummary } from "@/lib/trust-score.functions";
import { Loader2, RefreshCcw, Shield } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/trust")({
  head: () => ({ meta: [{ title: "Trust scores — Admin — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminTrust,
});

type SortKey = "score_desc" | "score_asc" | "updated_desc";

function AdminTrust() {
  const listFn = useServerFn(adminListTrustScores);
  const refreshFn = useServerFn(adminRefreshAllTrustScores);
  const [rows, setRows] = useState<TrustScoreRow[]>([]);
  const [summary, setSummary] = useState<TrustScoreSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sort, setSort] = useState<SortKey>("score_desc");
  const [search, setSearch] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await listFn({ data: { sort, limit: 100, search: search || undefined } });
      setRows(res.rows);
      setSummary(res.summary);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load trust scores");
    } finally {
      setLoading(false);
    }
  }, [listFn, sort, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const { updated } = await refreshFn();
      toast.success(`Recomputed ${updated} Helper trust score${updated === 1 ? "" : "s"}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-3xl font-bold"><Shield className="h-7 w-7 text-primary" /> Trust scores</h1>
            <p className="mt-1 text-sm text-muted-foreground">Proprietary Helper trust score, 0–100, computed from verification, completed jobs, review quality, response speed, reliability and repeat customers.</p>
          </div>
          <Button onClick={onRefresh} disabled={refreshing} variant="secondary">
            {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
            Recompute all
          </Button>
        </div>

        {summary && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard label="Active Helpers" value={summary.total.toLocaleString()} />
            <StatCard label="Median score" value={String(summary.median)} />
            <StatCard label="Elite (85+)" value={String(summary.elite)} tone="emerald" />
            <StatCard label="Trusted (70–84)" value={String(summary.trusted)} tone="sky" />
            <StatCard label="Rising (50–69)" value={String(summary.rising)} tone="amber" />
            <StatCard label="New (<50)" value={String(summary.newcomers)} />
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <input
            type="search"
            placeholder="Search by name or city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 w-full max-w-xs rounded-lg border border-border bg-card px-3 text-sm"
            aria-label="Search Helpers"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="h-10 rounded-lg border border-border bg-card px-3 text-sm"
            aria-label="Sort"
          >
            <option value="score_desc">Highest score first</option>
            <option value="score_asc">Lowest score first</option>
            <option value="updated_desc">Recently updated</option>
          </select>
          {summary?.last_refresh && (
            <span className="ml-auto text-xs text-muted-foreground">Last computed {new Date(summary.last_refresh).toLocaleString()}</span>
          )}
        </div>

        <section className="mt-4 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
          ) : err ? (
            <div className="px-6 py-10 text-sm text-destructive">{err}</div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-10 text-sm text-muted-foreground">No Helpers found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Helper</th>
                    <th className="px-4 py-3">Trust</th>
                    <th className="px-4 py-3">Rating</th>
                    <th className="px-4 py-3">Jobs</th>
                    <th className="px-4 py-3">Response</th>
                    <th className="px-4 py-3">Completion</th>
                    <th className="px-4 py-3">Repeat</th>
                    <th className="px-4 py-3">Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.helper_id} className="border-t border-border">
                      <td className="px-4 py-3">
                        <Link to="/helpers/$helperId" params={{ helperId: r.helper_id }} className="flex items-center gap-3 hover:underline">
                          {r.avatar_url ? (
                            <img src={r.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" loading="lazy" />
                          ) : (
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-accent text-xs font-semibold text-primary">{r.display_name.slice(0, 1)}</span>
                          )}
                          <span>
                            <span className="block font-medium text-foreground">{r.display_name}{r.suspended && <span className="ml-2 text-xs text-destructive">suspended</span>}</span>
                            <span className="block text-xs text-muted-foreground">{r.city ?? "—"}</span>
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-3"><TrustScore score={r.trust_score} size="sm" /></td>
                      <td className="px-4 py-3">{r.rating_count > 0 ? `${r.avg_rating.toFixed(2)}★ (${r.rating_count})` : "—"}</td>
                      <td className="px-4 py-3">{r.jobs_completed}<span className="text-muted-foreground"> / {r.jobs_cancelled} cnx</span></td>
                      <td className="px-4 py-3">{r.response_time_minutes != null ? `${r.response_time_minutes} min` : "—"}</td>
                      <td className="px-4 py-3">{r.completion_rate != null ? `${Math.round(r.completion_rate * 100)}%` : "—"}</td>
                      <td className="px-4 py-3">{r.repeat_booking_rate != null ? `${Math.round(r.repeat_booking_rate * 100)}%` : "—"}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className={r.id_verified ? "text-emerald-700" : "text-muted-foreground"}>ID</span>
                        {" · "}
                        <span className={r.background_check_passed ? "text-emerald-700" : "text-muted-foreground"}>DBS</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: "emerald" | "sky" | "amber" }) {
  const toneClass =
    tone === "emerald" ? "border-emerald-200 bg-emerald-50/60"
    : tone === "sky" ? "border-sky-200 bg-sky-50/60"
    : tone === "amber" ? "border-amber-200 bg-amber-50/60"
    : "border-border bg-card";
  return (
    <div className={`rounded-xl border ${toneClass} p-3`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}
